import { useState, useCallback } from "react";

/**
 * Centralised modal controller — handles
 *   • delete / danger confirmations
 *   • generic success confirmations
 *
 *  - Each modal stores optional i18n payload so
 *    translated “rich-text” fragments can be passed
 *    straight through from `StatusMessages`.
 */
export function useCommonModals() {
  const [modals, setModals] = useState({
    delete: {
      isOpen: false,
      title: "",
      titleData: {}, // { key, params } for i18n.rich
      message: "",
      messageData: {}, // { key, params } for i18n.rich
      confirmText: "",
      onConfirm: () => {},
      data: null, // anything you need later (ids, row, etc.)
      type: "", // 'delete' | 'status_change' | …
    },
    success: {
      isOpen: false,
      title: "",
      confirmTitleData: {}, // i18n data for <h3>
      message: "",
      confirmMsgData: {}, // i18n data for <p>
      confirmText: "",
      onConfirm: () => {},
      data: null,
      type: "",
    },
  });

  /** Open a “delete / danger” modal */
  const openDeleteModal = useCallback((config) => {
    setModals((prev) => ({
      ...prev,
      delete: {
        ...prev.delete,
        ...config,
        isOpen: true,
      },
    }));
  }, []);

  /** Close & reset the same modal */
  const closeDeleteModal = useCallback(() => {
    setModals((prev) => ({
      ...prev,
      delete: {
        ...prev.delete,
        isOpen: false,
        title: "",
        titleData: {},
        message: "",
        messageData: {},
        confirmText: "",
        onConfirm: () => {},
        data: null,
        type: "",
      },
    }));
  }, []);

  /** Open a generic “success” modal */
  const openSuccessModal = useCallback((config) => {
    setModals((prev) => ({
      ...prev,
      success: {
        ...prev.success,
        ...config,
        isOpen: true,
      },
    }));
  }, []);

  /** Close & reset the success modal */
  const closeSuccessModal = useCallback(() => {
    setModals((prev) => ({
      ...prev,
      success: {
        ...prev.success,
        isOpen: false,
        title: "",
        confirmTitleData: {},
        message: "",
        confirmMsgData: {},
        confirmText: "",
        onConfirm: () => {},
        data: null,
        type: "",
      },
    }));
  }, []);

  return {
    modals,
    openDeleteModal,
    closeDeleteModal,
    openSuccessModal,
    closeSuccessModal,
  };
}
